import { FormControl } from "@ecco/ui/form-control";
import { FormLabel } from "@ecco/ui/form-label";
import { InputGroup } from "@ecco/ui/input-group";
import { Field } from "formik";
import { Datepicker } from "@ecco/ui/datepicker";
import { styles } from "./baseFormStyles";
import { useState, useEffect, useMemo } from "react";
import { formatDateForDatePicker, getDateFormat } from "@lib/utils/dateutils";
import type { SingleFieldProps } from "./baseFormsValidations";
import { FieldIcon } from "./BaseInputField";
import { InputRightElement } from "@ecco/ui/input-element";
import { Input } from "@ecco/ui/input";

type SingleFieldBase = Pick<SingleFieldProps, "isSuccess" | "isInvalid" | "rightIcon">;
interface DeliveryDatePickerProps extends SingleFieldBase {
    header: string;
    prefix: string;
    testId: string;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    deliveryDates: Array<string>;
    initialValue?: string;
    locale?: string;
    isInvalid?: boolean;
    isSuccess?: boolean;
}

interface CalendarProps extends Pick<
    DeliveryDatePickerProps,
    "setFieldValue" | "deliveryDates" | "initialValue" | "locale" | "header"
> {
    fieldName: string;
}

const Calendar = ({
    setFieldValue,
    fieldName,
    deliveryDates,
    initialValue,
    locale,
}: CalendarProps) => {
    const includeDates = useMemo(() => {
        return deliveryDates.map((dateStr) => new Date(dateStr));
    }, [deliveryDates]);

    const [startDate, setStartDate] = useState<Date | null>(null);

    useEffect(() => {
        if (initialValue) {
            setStartDate(new Date(initialValue));
        }
    }, [initialValue]);

    // Set default value from includeDates if no initial value is provided or the initialValue is not in the list of includeDates,
    // and includeDates is not empty. This logic for setting default value needs to be in separate useEffect to avoid re-rendering of
    // Datepicker component.
    useEffect(() => {
        const initialDate = new Date(initialValue);
        const isInitialDateIncluded = includeDates.some(
            (date) => date.getTime() === initialDate.getTime()
        );

        if (
            (!initialValue || !isInitialDateIncluded) &&
            includeDates.length > 0 &&
            includeDates[0]
        ) {
            setStartDate(includeDates[0]);
            const formattedDate = formatDateForDatePicker(includeDates[0]);

            setFieldValue(fieldName, formattedDate);
        }
    }, [includeDates, initialValue, setFieldValue, deliveryDates]);

    const updateFieldValue = (date: Date | null) => {
        if (!date) {
            setStartDate(null);
            setFieldValue(fieldName, "");
            return;
        }
        const formattedDate = formatDateForDatePicker(date);
        setStartDate(date);
        setFieldValue(fieldName, formattedDate);
    };

    return (
        <Datepicker
            customInput={
                <Field
                    as={Input}
                    // Has to be type text becuase formatting in some countries doesn't work with type date.
                    type="text"
                    id={fieldName}
                    name={fieldName}
                    sx={styles.formInput}
                    //fieldName={fieldName}
                    autoComplete="off"
                    locale={locale}
                />
            }
            selected={startDate}
            includeDates={includeDates}
            onChange={(date) => updateFieldValue(date)}
            locale={locale}
            dateFormat={getDateFormat(locale)}
        />
    );
};

const DeliveryDatePicker = ({
    header,
    testId,
    prefix,
    setFieldValue,
    deliveryDates,
    initialValue,
    locale,
    isSuccess,
    isInvalid,
    rightIcon,
}: DeliveryDatePickerProps) => {
    const fieldName = prefix + "date";
    const iconProps = {
        rightIcon,
        isInvalid,
        isSuccess,
        ariaLabel: header,
    };
    return (
        <FormControl isInvalid={isInvalid} isSuccess={isSuccess} isRequired={false}>
            <FormLabel htmlFor="date" sx={styles.formLabel}>
                {header}
            </FormLabel>
            <InputGroup>
                <Field
                    data-testid={testId}
                    as={Calendar}
                    type="Date"
                    id={fieldName}
                    name={fieldName}
                    sx={styles.formInput}
                    setFieldValue={setFieldValue}
                    fieldName={fieldName}
                    deliveryDates={deliveryDates}
                    initialValue={initialValue}
                    locale={locale}
                    rightIcon={rightIcon}
                    isInvalid={isInvalid}
                    isSuccess={isSuccess}
                    header={header}
                />
                {rightIcon && (
                    <InputRightElement h={"100%"}>
                        <FieldIcon {...iconProps} />
                    </InputRightElement>
                )}
            </InputGroup>
        </FormControl>
    );
};

export default DeliveryDatePicker;
